/* ────────────────────────────────────────────────────────────────
   src/views/admin/users/attendance/index.jsx
   Attendance page - Standard extra_filter pattern
   ──────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import ToastMe from '@/components/ui/ToastMe';
import validationErrors from '@/lib/validationErrors';

import Breadcrumb        from '@/components/common/Breadcrumb';
import CustomDropdown    from '@/components/common/CustomDropdown';
import Card              from '@/components/ui/cards/Card';
import CardHeader        from '@/components/ui/cards/CardHeader';
import PerPageSelection  from '@/components/ui/tables/PerPageSelection';
import { cn }            from '@/lib/utils';

import DataTableFilter     from './component/DataTableFilter';
import DataTable           from './component/DataTable';
import AddAttendanceDrawer from './component/AddAttendanceDrawer';
import { getAttendanceService, deleteAttendanceService, updateAttendanceService } from '@/services/AttendanceService';

export default function AttendancePage({ pageTitle }) {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { userData: user, token: reduxTok } = useSelector(s => s.Auth);
  const token     = reduxTok || localStorage.getItem('jwt_token');

  // ─── STATE ───────────────────────────────────────────────────
  const [dataSource, setDataSource]     = useState([]);
  const [totalRecord, setTotalRecord]   = useState(0);
  const [activeDrawer, setActiveDrawer] = useState(false);
  const [editDataId, setEditDataId]     = useState('');
  const [isLoading, setIsLoading]       = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterDivRef                    = useRef(null);

  const [dataFilter, setDataFilter] = useState({
    search: '',
    page: 1,
    limit: 10,
    extra_filter: {},
  });

  // ─── PERMISSIONS ─────────────────────────────────────────────
  const canSeeAll = !!(
    user?.isSuperAdmin ||
    user?.permissions?.includes('view_attendance_all')
  );

  // ─── EFFECT: LOAD DATA ───────────────────────────────────────
  useEffect(() => {
    if (!token) {
      navigate('/sign-in');
      return;
    }
    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFilter, token]);

  function loadTableData() {
    setIsLoading(true);
    dispatch(getAttendanceService(dataFilter))
      .then((res) => {
        if (res?.data?.success) {
          const { data, total, page, limit } = res.data;
          setDataSource(Array.isArray(data) ? data : []);
          setTotalRecord(Number(total ?? 0));
          if (page && page !== dataFilter.page) {
            setDataFilter(f => ({ ...f, page }));
          }
          if (limit && limit !== dataFilter.limit) {
            setDataFilter(f => ({ ...f, limit }));
          }
        } else {
          ToastMe(res?.data?.message || 'Failed to load attendance', 'error');
          setDataSource([]);
          setTotalRecord(0);
        }
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        ToastMe(message || 'Error loading attendance', 'error');
        setDataSource([]);
        setTotalRecord(0);
      })
      .finally(() => setIsLoading(false));
  }

  // ─── DELETE ──────────────────────────────────────────────────
  async function deleteRecord(id) {
    const { isConfirmed } = await Swal.fire({
      title            : 'Delete this record?',
      icon             : 'warning',
      showCancelButton : true,
      confirmButtonText: 'Yes, delete',
    });
    if (!isConfirmed) return;

    setIsLoading(true);
    dispatch(deleteAttendanceService(id))
      .then(() => {
        Swal.fire('Deleted!', '', 'success');
        loadTableData();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        ToastMe(message || 'Error deleting attendance', 'error');
      })
      .finally(() => setIsLoading(false));
  }

  // ─── CHANGE STATUS (always available if permitted) ───────────
  async function handleStatusUpdate(row) {
    const options = {
      Present: 'Present',
      Late: 'Late',
      Absent: 'Absent',
      Leave: 'Leave',
      Off: 'Off',
      HalfDay: 'HalfDay',
      Other: 'Other',
    };

    const { value } = await Swal.fire({
      title            : 'Change attendance status',
      input            : 'select',
      inputOptions     : options,
      inputValue       : row.status || 'Absent',
      inputPlaceholder : 'Select status',
      showCancelButton : true,
      confirmButtonText: 'Update',
      customClass      : {
        popup         : 'rounded-lg',
        confirmButton : 'db-btn bg-primary',
        cancelButton  : 'db-btn bg-secondary ml-2',
        input         : 'db-input',
      },
    });
    if (!value) return;

    setIsLoading(true);
    dispatch(updateAttendanceService(row._id, { status: value }))
      .then(() => {
        Swal.fire('Updated!', '', 'success');
        loadTableData();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        ToastMe(message || 'Update failed', 'error');
      })
      .finally(() => setIsLoading(false));
  }

  // ─── DRAWER HANDLERS ─────────────────────────────────────────
  function handleDrawerOpen(id = '') {
    setEditDataId(id);
    setActiveDrawer(true);
    document.body.style.overflowY = 'hidden';
  }
  function handleDrawerClose() {
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    loadTableData();
  }

  // ─── QUICK FILTERS ───────────────────────────────────────────
  function applyQuickFilter(type) {
    setDataFilter(f => ({
      search: '',
      page: 1,
      limit: f.limit,
      extra_filter: type === 'today'
        ? { date: dayjs().format('YYYY-MM-DD') }
        : type === 'yesterday'
        ? { date: dayjs().subtract(1, 'day').format('YYYY-MM-DD') }
        : type === 'last7'
        ? { period: '7days' }
        : {}, // "All Time" passes empty extra_filter
    }));
  }

  // ─── FILTER HANDLER ─────────────────────────────────────────
  const onChangeFilter = (name, value) => {
    if (name === 'extra_filter') {
      setDataFilter((prev) => ({
        ...prev,
        extra_filter: { ...prev.extra_filter, ...value },
      }));
    } else {
      setDataFilter((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ─── HELPER: prettier employee name ──────────────────────────
  const employeeName = (emp) =>
    emp?.name ||
    `${emp?.first_name ?? ''} ${emp?.last_name ?? ''}`.trim() ||
    '';

  const filteredData = dataSource.filter(row => employeeName(row.employee) !== '');

  // ─── COLUMNS ─────────────────────────────────────────────────
  const tableColumn = [
    ...(canSeeAll
      ? [{
          name           : 'Employee',
          isIncludePrint : true,
          cell           : row => employeeName(row.employee),
        }]
      : []),
    { name: 'Date',              isIncludePrint: true },
    { name: 'Expected In Time',  isIncludePrint: true },
    { name: 'In Time',           isIncludePrint: true },
    { name: 'Expected Out Time', isIncludePrint: true },
    { name: 'Out Time',          isIncludePrint: true },
    { name: 'Status',            isIncludePrint: true },
    ...(canSeeAll ? [{ name: 'Action', isIncludePrint: false }] : []),
  ];

  if (!token) {
    return (
      <p style={{ padding: 20, textAlign: 'center' }}>
        You’re not signed in. Please{' '}
        <a href="/sign-in" className="underline text-blue-600">
          sign in
        </a>{' '}
        to view attendance.
      </p>
    );
  }

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />

      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>
          <div className="db-card-filter">
            <PerPageSelection
              onChangeParPage={(n, v) => setDataFilter(f => ({ ...f, [n]: v }))}
              limit={dataFilter.limit}
            />
            <button
              onClick={() => {
                if (!filterDivRef.current) return;
                filterDivRef.current.style.height = isFilterOpen ? '0px' : '100%';
                setIsFilterOpen(!isFilterOpen);
              }}
              className="db-card-filter-btn"
            >
              <i className="lab lab-filter" /> <span>Filter</span>
            </button>
            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[]}
            />
          </div>
        </CardHeader>

        <div className="p-4 flex gap-3">
          <button className="db-btn bg-secondary" onClick={() => applyQuickFilter('today')}>
            Today
          </button>
          <button className="db-btn bg-secondary" onClick={() => applyQuickFilter('yesterday')}>
            Yesterday
          </button>
          <button className="db-btn bg-secondary" onClick={() => applyQuickFilter('last7')}>
            Last&nbsp;7&nbsp;Days
          </button>
          <button className="db-btn bg-secondary" onClick={() => applyQuickFilter('all')}>
            All&nbsp;Time
          </button>
        </div>

        <div
          className={cn('table-filter-div', isFilterOpen && 'active')}
          ref={filterDivRef}
        >
          <DataTableFilter
            onChangeFilter={onChangeFilter}
            showEmployeeSelect={canSeeAll}
          />
        </div>

        {isLoading ? (
          <p style={{ padding: 20, textAlign: 'center' }}>Loading attendance…</p>
        ) : filteredData.length > 0 ? (
          <DataTable
            isLoading={isLoading}
            tableColumn={tableColumn}
            dataSource={filteredData}
            totalRecord={totalRecord}
            dataFilter={dataFilter}
            onChangePage={(n, v) => setDataFilter(f => ({ ...f, [n]: v }))}
            deleteRecord={canSeeAll ? deleteRecord : undefined}
            handleStatusUpdate={canSeeAll ? handleStatusUpdate : undefined}
            handleDrawerOpen={canSeeAll ? handleDrawerOpen : undefined}
          />
        ) : (
          <p style={{ padding: 20, textAlign: 'center' }}>
            {canSeeAll
              ? 'No attendance records found.'
              : 'You have no attendance records.'}
          </p>
        )}

        {activeDrawer && (
          <AddAttendanceDrawer
            titleText={pageTitle}
            activeDrawer={activeDrawer}
            setActiveDrawer={open =>
              open ? handleDrawerOpen(editDataId) : handleDrawerClose()
            }
            editDataId={editDataId}
            getTableData={loadTableData}
            token={token}
          />
        )}
      </Card>
    </>
  );
}

